import SeoBoundary from 'lib/boundaries/SeoBoundary'
import { deleteAllCookies } from 'lib/hooks/useLogout'
import { IDefaultLayout } from 'lib/layouts/DefaultLayout/types'
import AlternativePaymentsLogo from 'lib/ui/Icon'
import Text from 'lib/ui/Text'
import Image from 'next/image'
import {
  getEnvironmentByDomain,
  getPartnerAppDomain,
  paymentsPortalLocalUrl,
  Environment
} from 'utils/env'

import { usePartnerContext } from '@/hooks/providers/PartnerProvider'
import Button from '@/ui/Button/v2'

import * as css from './styles'

interface IErrorLayout extends Pick<IDefaultLayout, 'headline' | 'title'> {
  message?: React.ReactNode
}
const ErrorLayout = ({
  headline = 'Sorry',
  message = 'You do not have access to this resource or this page does not exist.',
  title = 'Alternative Payments - Page does not exist'
}: IErrorLayout) => {
  const { partner, loading } = usePartnerContext()
  // Same ladder as the server-side auto-restart: the partner's payments
  // portal when the partner-id-auth cookie resolves, else the partner app.
  // Both mint a fresh OAuth flow, so this button is always a working exit —
  // even when the automatic restart budget has been exhausted.
  const handleBackToDashboard = () => {
    deleteAllCookies()
    const isPartner = !partner

    if (isPartner) {
      // Safe: getPartnerAppDomain constructs URL from known domain patterns, no user input
      window.location.href = getPartnerAppDomain(window.location.origin)
      return
    }

    const isDevelopment =
      getEnvironmentByDomain(window.location.origin) === Environment.Local
    // Canonical portal domain first — vanity CNAMEs are not registered
    // OAuth redirect_uris
    const url = isDevelopment
      ? paymentsPortalLocalUrl
      : 'https://' +
        (partner.paymentsPortalDefaultUrl || partner.paymentsPortalUrl)
    // Safe: url is either paymentsPortalLocalUrl (constant) or 'https://' + API domain (hardcoded protocol)
    window.location.href = url
  }

  return (
    <SeoBoundary title={title}>
      <div data-testid='error-page' css={css.body}>
        <div css={css.messageWrapper}>
          <div css={css.logo}>
            <AlternativePaymentsLogo
              name='alternativePaymentsLogo'
              width={15}
              height={5}
              viewBox='0 0 170 35'
              color='black'
            />
          </div>
          <Text styles={css.title}>{headline}</Text>
          <Text styles={css.message}>{message}</Text>
          {!loading && (
            <Button styles={css.button} onClick={handleBackToDashboard}>
              Back to Dashboard
            </Button>
          )}
        </div>
        <div css={css.imageContainer}>
          <div css={css.imageWrapper}>
            <Image
              src='/error-hero-image.png'
              alt={title}
              fill
              objectFit='contain'
            />
          </div>
        </div>
      </div>
    </SeoBoundary>
  )
}

export default ErrorLayout
